<script setup>
import { computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { useUVXFeaturesAccess } from 'dashboard/composables/wgpt/useUVXFeaturesAccess';

import Input from 'next/input/Input.vue';
import TextArea from 'next/textarea/TextArea.vue';
import Button from 'next/button/Button.vue';
import ParameterRow from '../../components/ParameterRow.vue';
import MainPromptEditor from './MainPromptEditor.vue';
import MainAgentSlotFields from './MainAgentSlotFields.vue';
import {
  invalidPromptVariables,
  knownPromptParameters,
} from '../helpers/promptVariables';
import {
  initialSlotConfig,
  slotConfigPayload,
} from '../helpers/mainAgentConfig';

const props = defineProps({
  workflow: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();
const { hasUVXFeaturesAccess } = useUVXFeaturesAccess();
const llmConfigs = useMapGetter('wgptBotWorkflows/getLlmConfigs');

const initialState = {
  name: props.workflow.name ?? '',
  description: props.workflow.description ?? '',
  main_prompt: props.workflow.main_prompt ?? '',
  config: {
    main_agent: {
      primary: initialSlotConfig(
        props.workflow.config?.main_agent?.primary,
        llmConfigs.value
      ),
      secondary: initialSlotConfig(
        props.workflow.config?.main_agent?.secondary,
        llmConfigs.value
      ),
    },
  },
  parameters: Object.entries(props.workflow.parameters ?? {}).map(
    ([key, value], index) => ({
      key: key ?? '',
      value: value ?? '',
      disabled: true,
      index,
    })
  ),
};

const state = reactive({ ...initialState });

const mainPromptParameterNames = computed(() =>
  knownPromptParameters(state.parameters.map(parameter => parameter.key))
);

const invalidMainPromptVariables = computed(() =>
  invalidPromptVariables(state.main_prompt, mainPromptParameterNames.value)
);

const rules = {
  name: { required, minLength: minLength(1) },
  description: {},
  main_prompt: {
    required: requiredIf(() => hasUVXFeaturesAccess.value),
    validVariables: () =>
      !hasUVXFeaturesAccess.value ||
      invalidMainPromptVariables.value.length === 0,
  },
  parameters: {
    hasDuplicates: value => {
      const keys = value.map(p => p.key);
      return !keys.some((key, index) => keys.indexOf(key) !== index);
    },
  },
};

const v$ = useVuelidate(rules, state);

const mainPromptErrorMessage = computed(() => {
  if (!v$.value.main_prompt.$error) return '';
  if (v$.value.main_prompt.required.$invalid) {
    return t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.EDIT.FORM.MAIN_PROMPT.ERROR');
  }
  return t(
    'WGPT_BOTS.EDIT.TABS.WORKFLOWS.EDIT.FORM.MAIN_PROMPT.INVALID_VARIABLES_ERROR',
    { variables: invalidMainPromptVariables.value.join(', ') }
  );
});

const prepareFormData = () => {
  const parameters = {};
  state.parameters.forEach(parameter => {
    if (parameter.key) {
      parameters[parameter.key] = parameter.value;
    }
  });

  const data = {
    name: state.name,
    description: state.description,
    parameters,
  };
  if (hasUVXFeaturesAccess.value) {
    data.main_prompt = state.main_prompt;
    data.config = {
      main_agent: {
        primary: slotConfigPayload(state.config.main_agent.primary),
        secondary: slotConfigPayload(state.config.main_agent.secondary),
      },
    };
  }
  return data;
};

const validate = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return null;

  return prepareFormData();
};

const handleSubmit = async () => {
  const isFormValid = await validate();
  if (!isFormValid) return;

  emit('submit', prepareFormData());
};

const addParameter = () => {
  const parameter = {
    key: '',
    value: '',
    index: state.parameters.length,
  };
  state.parameters.push(parameter);
};

const removeParameter = index => {
  state.parameters = state.parameters.filter(
    parameter => parameter.index !== index
  );
};

const parameterHasDuplicate = index => {
  const key = state.parameters[index].key;
  const keys = state.parameters.map(p => p.key);
  return keys.filter(k => k === key).length > 1;
};

const onParameterChange = (index, parameter) => {
  state.parameters = state.parameters.map((parameterBefore, i) => {
    if (index !== i) return parameterBefore;
    return { ...parameterBefore, ...parameter };
  });
};

defineExpose({ validate });
</script>

<template>
  <form class="flex flex-col gap-4 pb-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.name"
      :label="t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.EDIT.FORM.NAME.LABEL')"
      :message-type="v$.name.$error ? 'error' : 'info'"
      :message="
        v$.name.$error
          ? t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.EDIT.FORM.NAME.ERROR')
          : ''
      "
    />

    <TextArea
      v-model="state.description"
      :label="t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.EDIT.FORM.DESCRIPTION.LABEL')"
      :message-type="v$.description.$error ? 'error' : 'info'"
      :message="
        v$.description.$error
          ? t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.EDIT.FORM.DESCRIPTION.ERROR')
          : ''
      "
      custom-text-area-class="!outline !outline-0"
      resize
    />

    <MainPromptEditor
      v-if="hasUVXFeaturesAccess"
      v-model="state.main_prompt"
      :parameters="mainPromptParameterNames"
      :label="t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.EDIT.FORM.MAIN_PROMPT.LABEL')"
      :message-type="mainPromptErrorMessage ? 'error' : 'info'"
      :message="mainPromptErrorMessage"
      @blur="v$.main_prompt.$touch"
    />

    <div
      v-if="hasUVXFeaturesAccess"
      class="grid grid-cols-1 gap-4 sm:grid-cols-2"
    >
      <MainAgentSlotFields
        v-model="state.config.main_agent.primary"
        :options="llmConfigs"
        :label="t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.MAIN_AGENT.PRIMARY.LABEL')"
      />
      <MainAgentSlotFields
        v-model="state.config.main_agent.secondary"
        :options="llmConfigs"
        :label="t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.MAIN_AGENT.SECONDARY.LABEL')"
      />
    </div>

    <div class="w-full mt-4">
      <label>
        <span>{{
          $t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.EDIT.FORM.PARAMETERS.LABEL')
        }}</span>
      </label>
    </div>
    <ParameterRow
      v-for="(parameter, index) in state.parameters"
      :key="parameter.index"
      :value="parameter"
      :has-duplicate="parameterHasDuplicate(index)"
      :show-label="index === 0"
      :disabled="parameter.disabled"
      @remove="removeParameter(parameter.index)"
      @change="e => onParameterChange(index, e)"
    />

    <div class="mt-2 -ml-1">
      <Button
        :label="
          $t(
            'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CREATE.ADD_PARAMETER_BTN_TXT'
          )
        "
        size="xs"
        variant="link"
        icon="i-lucide-plus"
        @click.prevent="addParameter"
      />
    </div>
  </form>
</template>
